import { useEffect, useState } from "react";
import { toast } from "sonner";
import ProtectedImage from "@/components/ProtectedImage";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MAX_PHOTO = 8 * 1024 * 1024;

// Form tambah/edit alat: grup identitas+penyimpanan dan foto, kontrol
// berlabel, validasi saat blur dan submit, ringkasan error yang difokuskan,
// input dan file dipertahankan saat gagal simpan.
export default function ItemForm({
  value, onChange, categories, prefix,
  photoFile, onPhotoFileChange, uploadedPath, uploadPct,
  errors = {}, onBlurField,
}) {
  const [preview, setPreview] = useState(null);
  const err = (name) => errors[name];

  useEffect(() => {
    if (!photoFile) { setPreview(null); return undefined; }
    const url = URL.createObjectURL(photoFile);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  const selectFile = (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    if (!selected.type.startsWith("image/")) {
      toast.error("File harus berupa gambar");
      event.target.value = "";
      return;
    }
    if (selected.size > MAX_PHOTO) {
      toast.error("Foto maksimal 8 MB");
      event.target.value = "";
      return;
    }
    onPhotoFileChange(selected);
  };

  const photoStage = uploadPct != null
    ? `Mengunggah ${uploadPct}%…`
    : uploadedPath
      ? "Foto sudah terunggah, dipakai ulang tanpa unggah ulang."
      : photoFile
        ? `Siap diunggah: ${photoFile.name}`
        : value.photo
          ? "Foto saat ini dipertahankan kecuali diganti."
          : "Belum ada foto. JPG, PNG, WebP, GIF, atau HEIC. Maksimal 8 MB.";

  const field = (name, label, required, input) => (
    <div className="space-y-1.5">
      <Label htmlFor={`${prefix}-${name}`} className="text-sm font-medium text-zinc-300">
        {label} {required && <span aria-hidden className="text-rose-400">*</span>}
      </Label>
      {input}
      {err(name) && <p role="alert" className="text-xs text-rose-400">{err(name)}</p>}
    </div>
  );

  const inputCls = (name) =>
    `h-12 rounded-xl border bg-zinc-950 text-sm ${err(name) ? "border-rose-500" : "border-white/10"}`;

  return (
    <div className="space-y-5">
      <fieldset className="space-y-4 rounded-2xl border border-white/10 p-4">
        <legend className="px-1 text-xs font-semibold text-zinc-400">Identitas dan penyimpanan</legend>
        <div className="grid gap-4 sm:grid-cols-2">
          {field("name", "Nama alat", true,
            <Input id={`${prefix}-name`} data-testid={`${prefix}-name-input`} value={value.name || ""}
              onChange={(e) => onChange({ ...value, name: e.target.value })}
              onBlur={() => onBlurField?.("name")}
              aria-invalid={!!err("name")}
              className={inputCls("name")} />)}
          {field("item_code", "Kode alat", true,
            <Input id={`${prefix}-item_code`} data-testid={`${prefix}-code-input`} value={value.item_code || ""}
              onChange={(e) => onChange({ ...value, item_code: e.target.value })}
              onBlur={() => onBlurField?.("item_code")}
              aria-invalid={!!err("item_code")}
              className={`${inputCls("item_code")} font-mono`} />)}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={`${prefix}-category`} className="text-sm font-medium text-zinc-300">
              Kategori <span aria-hidden className="text-rose-400">*</span>
            </Label>
            <Select value={value.category || ""} onValueChange={(v) => onChange({ ...value, category: v })}>
              <SelectTrigger id={`${prefix}-category`} data-testid={`${prefix}-category-select`}
                aria-invalid={!!err("category")}
                className={`${inputCls("category")} ${!value.category ? "text-zinc-500" : ""}`}>
                <SelectValue placeholder="Pilih kategori" />
              </SelectTrigger>
              <SelectContent className="max-h-60 border-white/10 bg-zinc-900 text-zinc-100">
                {categories.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {err("category") && <p role="alert" className="text-xs text-rose-400">{err("category")}</p>}
          </div>
          {field("location", "Lokasi penyimpanan", false,
            <Input id={`${prefix}-location`} data-testid={`${prefix}-location-input`} value={value.location || ""}
              onChange={(e) => onChange({ ...value, location: e.target.value })}
              className={inputCls("location")} />)}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {field("condition", "Kondisi", false,
            <Input id={`${prefix}-condition`} data-testid={`${prefix}-condition-input`} value={value.condition || ""}
              onChange={(e) => onChange({ ...value, condition: e.target.value })}
              className={inputCls("condition")} />)}
          {field("quantity", "Jumlah stok", true,
            <Input id={`${prefix}-quantity`} data-testid={`${prefix}-quantity-input`} type="number" min="1" step="1"
              value={value.quantity ?? 1}
              onChange={(e) => onChange({ ...value, quantity: e.target.value === "" ? "" : Number(e.target.value) })}
              onBlur={() => onBlurField?.("quantity")}
              aria-invalid={!!err("quantity")}
              className={`${inputCls("quantity")} tabular-nums`} />)}
        </div>
      </fieldset>

      <fieldset className="space-y-3 rounded-2xl border border-white/10 p-4">
        <legend className="px-1 text-xs font-semibold text-zinc-400">Foto alat (wajib)</legend>
        <div className="flex flex-wrap items-center gap-4">
          <ProtectedImage
            src={preview || value.photo}
            alt="Pratinjau foto alat"
            className="h-20 w-20 rounded-xl border border-white/10 object-cover"
          />
          <div className="min-w-0 flex-1 space-y-1.5">
            <label
              htmlFor={`${prefix}-photo`}
              className="inline-flex h-11 cursor-pointer items-center rounded-full bg-[#0069DB] px-5 text-sm font-semibold text-white hover:bg-[#0058B6]"
            >
              {value.photo || photoFile ? "Ganti foto" : "Pilih foto"}
            </label>
            <Input
              id={`${prefix}-photo`}
              data-testid={`${prefix}-photo-input`}
              type="file"
              accept="image/*"
              onChange={selectFile}
              aria-invalid={!!err("photo")}
              className="hidden"
            />
            <p className="text-xs text-zinc-500" role="status">{photoStage}</p>
            {err("photo") && <p role="alert" className="text-xs text-rose-400">{err("photo")}</p>}
          </div>
        </div>
      </fieldset>
    </div>
  );
}

export function validateItem(value, { hasPhoto }) {
  const errors = {};
  if (!String(value.name || "").trim()) errors.name = "Isi nama alat.";
  if (!String(value.item_code || "").trim()) errors.item_code = "Isi kode alat yang unik.";
  if (!String(value.category || "").trim()) errors.category = "Pilih kategori.";
  const qty = Number(value.quantity);
  if (value.quantity === "" || !Number.isInteger(qty) || qty < 1) errors.quantity = "Jumlah stok minimal 1.";
  if (!hasPhoto) errors.photo = "Foto alat wajib diunggah.";
  return errors;
}
